import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * StatCard - Individual metric card
 */
interface StatCardProps {
  label: string;
  value: number;
  tint?: 'success' | 'primary' | 'neutral';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, tint = 'neutral' }) => {
  const tintColors = {
    success: {
      bg: 'rgba(5, 150, 105, 0.08)',
      text: 'success.main',
    },
    primary: {
      bg: 'rgba(99, 102, 241, 0.08)',
      text: 'primary.main',
    },
    neutral: {
      bg: 'grey.50',
      text: 'text.primary',
    },
  };

  const colors = tintColors[tint];

  return (
    <Box
      sx={{
        padding: '12px 16px',
        borderRadius: '8px',
        backgroundColor: colors.bg,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 700,
          color: colors.text,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

/**
 * FeatureStudioStats - 4-card metrics strip
 *
 * Shows the same stat types for all selection levels:
 * - Work Products, Available, Ready, Generating
 */
export interface FeatureStudioStatsProps {
  level: 'product' | 'feature' | 'story';
  stats: {
    card1: { label: string; value: number };
    card2: { label: string; value: number };
    ready: number;
    generating: number;
  };
}

export const FeatureStudioStats: React.FC<FeatureStudioStatsProps> = ({ level, stats }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 2,
        padding: '18px 28px',
        backgroundColor: 'background.default',
      }}
    >
      <StatCard label={stats.card1.label} value={stats.card1.value} tint="neutral" />
      <StatCard label={stats.card2.label} value={stats.card2.value} tint="neutral" />
      <StatCard label="Ready" value={stats.ready} tint="success" />
      <StatCard label="Generating" value={stats.generating} tint="primary" />
    </Box>
  );
};
